import { Stack } from '@mui/material'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { MyPvIo, OptimizationMode } from '../../../installation/forms'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useAccordionStyle from 'src/Libs/styles'
import { SettingsBox, SettingInfoBox } from 'src/Elements/Data'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { TFunction } from 'i18next'
import useModals from 'src/Hooks/useModals'
import { useBoiler } from '../hooks'
import { BoilerCalendar } from '../components'
import { useSystemSettingsTitleAndBreadcrumbs } from 'src/Libs/system-settings'
import { BlankView, BlankViewImageVariants } from 'src/Components/blank-view'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

//#region Wrapper
interface BoilerPageWrapperProps {
  installationId: number
  queryIsLoading: boolean
  myPv: MyPvIo | null
}

export const BoilerPageWrapper: React.FC<BoilerPageWrapperProps> = ({ installationId, queryIsLoading, myPv }) => {
  useSystemSettingsTitleAndBreadcrumbs({ pageTitleKey: 'framework.end-user.boiler' })

  if (!myPv && !queryIsLoading) {
    return (
      <BlankView
        title={'systemSettings.boiler.no-active-device.title'}
        explanation={'systemSettings.boiler.no-active-device.explanation'}
        imageVariant={BlankViewImageVariants.Worried}
      />
    )
  }

  if (!myPv) return null

  return <BoilerPage installationId={installationId} queryIsLoading={queryIsLoading} myPv={myPv} />
}

//#endregion Wrapper
//#region Page

interface BoilerPageProps {
  installationId: number
  queryIsLoading: boolean
  myPv: MyPvIo
}

/** User-level boiler page to manage a MyPV device. */
export const BoilerPage: React.FC<BoilerPageProps> = ({ installationId, queryIsLoading, myPv }) => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyle()
  const { openBoilerScheduleDialog } = useModals()
  const { isPending, updateControlMode, handleSaveBoostConfig, controlMode } = useBoiler({ installationId, myPv })
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const onOpenBoilerScheduleDialog = (): void => {
    openBoilerScheduleDialog({
      installationId: installationId,
      myPv: myPv,
      saveBoostConfig: handleSaveBoostConfig,
    })
  }

  const isLoading = queryIsLoading || isPending

  const energyOptions = getOptimizationModeOptions(t)
  const currentSelectedEnergyOption = energyOptions.find(item => item.value === controlMode)

  return (
    <>
      <BannerQueue location={BannerLocations.FlowSettingsBoiler} sx={{ mb: 2, mt: [2, 0] }} />
      <Stack direction={'column'}>
        <AccordionItem
          title={t('user-settings.boiler-page.settings-title')}
          expanded
          accordionDetailsSx={{ px: 4, py: 2 }}
          className={classes.accordion}
        >
          <Stack gap={2}>
            <SettingInfoBox description={t('user-settings.boiler-page.banner')} />
            <BoilerCalendar boostConfig={myPv.boostConfig} onClickEditCalendar={onOpenBoilerScheduleDialog} isLoading={isPending} />
          </Stack>
        </AccordionItem>
        <AccordionItem
          title={t('user-settings.boiler-page.energy-title')}
          expanded
          accordionDetailsSx={{ px: 4, py: 2 }}
          className={classes.accordion}
        >
          <SettingsBox
            title={t('user-settings.boiler-page.energy-description')}
            suffix={<Select options={energyOptions} value={controlMode} onChange={updateControlMode} disabled={isLoading || !userCanEdit} />}
            disabled={isLoading || !userCanEdit}
          />
          <Stack gap={2}>
            <SettingInfoBox title={currentSelectedEnergyOption?.title ?? ''} description={currentSelectedEnergyOption?.subtitle ?? ''} />
          </Stack>
        </AccordionItem>
      </Stack>
    </>
  )
}
//#endregion Page

//#region OptimizationMode Helper
interface OptimizationModeOption {
  value: OptimizationMode
  label: string
  title: string
  subtitle: string
}

const getOptimizationModeOptions = (t: TFunction): OptimizationModeOption[] => {
  return [
    {
      label: t('user-settings.boiler-page.options.security-temperature-short'),
      subtitle: t('user-settings.boiler-page.options.security-temperature-description'),
      title: t('user-settings.boiler-page.options.security-temperature'),
      value: 'No',
    },
    {
      label: t('user-settings.boiler-page.options.solar-surplus-short'),
      subtitle: t('user-settings.boiler-page.options.solar-surplus-description'),
      title: t('user-settings.boiler-page.options.solar-surplus'),
      value: 'Surplus',
    },
    {
      label: t('user-settings.boiler-page.options.solar-only-short'),
      subtitle: t('user-settings.boiler-page.options.solar-only-description'),
      title: t('user-settings.boiler-page.options.solar-only'),
      value: 'Solar',
    },
    {
      label: t('user-settings.boiler-page.options.boost-short'),
      subtitle: t('user-settings.boiler-page.options.boost-description'),
      title: t('user-settings.boiler-page.options.boost'),
      value: 'All',
    },
  ]
}
//#endregion
